import React, { useState } from 'react';
import { Check, Copy, Database } from 'lucide-react';
import { Badge } from 'src/shared/components/ui/badge';
import Tooltip from 'src/shared/components/tooltip/Tooltip';
import type { StacAsset } from 'src/shared/state/api-slices/stacCatalogueEngineSlice';
import { customFieldsOf, isTileSource } from '../../domain/assets/stacAssetFields';
import { MEDIA_TYPE_OPTIONS } from '../../domain/assets/assetVocabulary';

type AssetTableProps = {
    assets: Record<string, StacAsset>;
};

const renderCustomValue = (value: unknown): string => (typeof value === 'string' ? value : JSON.stringify(value));

/** The type and subtype, as RFC 9110 compares them: case-insensitive, parameters ignored. */
const nameOf = (declared: string) => declared.split(';')[0].trim().toLowerCase();

/**
 * What a media type is called, where we have a name for it.
 *
 * `image/tiff; application=geotiff; profile=cloud-optimized` is a line of punctuation to read
 * and a moment to recognise. "Cloud-Optimised GeoTIFF" is neither — and the exact value stays
 * a hover away for anyone who needs it.
 */
const mediaTypeLabel = (declared: string): string =>
    MEDIA_TYPE_OPTIONS.find(option => nameOf(option.value) === nameOf(declared))?.label ?? declared;

/**
 * One asset's object: an icon that carries the URI on hover, and a marker to copy it.
 *
 * The URI is long, repeats across every asset of every version, and is rarely what a reader
 * came for — an icon says the object is there and gives the address back on demand. Copying is
 * its own marker rather than a click on the icon, so showing and taking are separate acts.
 */
const AssetHref = ({ assetKey, href }: { assetKey: string; href: string }) => {
    const [isCopied, setIsCopied] = useState(false);

    const copy = async () => {
        await navigator.clipboard?.writeText(href);
        setIsCopied(true);
        window.setTimeout(() => setIsCopied(false), 1500);
    };

    return (
        // `relative` so the confirmation is positioned against this pair: floated rather than
        // inserted, or every asset below it shifts down the moment one is copied.
        <span className='relative inline-flex items-center gap-1'>
            <span className='font-medium'>uri:</span>
            <Tooltip content={<span className='break-all'>{href}</span>} placement='top' maxWidth={480}>
                <span
                    aria-label={`Object ${href}`}
                    data-testid={`asset-href-${assetKey}`}
                    className='inline-flex cursor-help items-center rounded bg-grey-10 p-1 text-muted-foreground'
                >
                    <Database className='h-3.5 w-3.5' aria-hidden='true' />
                </span>
            </Tooltip>
            <button
                type='button'
                onClick={copy}
                aria-label={`Copy the object address of ${assetKey}`}
                data-testid={`asset-href-copy-${assetKey}`}
                className='inline-flex items-center rounded p-1 text-muted-foreground hover:bg-secondary hover:text-primary'
            >
                {isCopied ? <Check className='h-3.5 w-3.5' aria-hidden='true' /> : <Copy className='h-3.5 w-3.5' aria-hidden='true' />}
            </button>
            {isCopied && (
                <span
                    className='absolute left-0 top-full z-10 mt-1 whitespace-nowrap rounded bg-foreground px-2 py-0.5 text-[10px] text-white'
                    data-testid={`asset-href-copied-${assetKey}`}
                    role='status'
                >
                    Copied
                </span>
            )}
        </span>
    );
};

/** The files of one version. Keys are the producer's and carry no meaning; `roles` says what a file is for. */
const AssetTable = ({ assets }: AssetTableProps) => {
    const entries = Object.entries(assets);

    if (entries.length === 0) {
        return (
            <div className='flex flex-col items-center py-10 gap-2 text-center' data-testid='asset-list-empty-state'>
                <h4 className='text-base font-semibold text-primary'>No files on this version</h4>
                <p className='text-sm text-grey-70'>The engine requires at least one, so this version cannot be served.</p>
            </div>
        );
    }

    return (
        <ul className='flex flex-col gap-2' data-testid='asset-list'>
            {entries.map(([key, asset]) => {
                const custom = Object.entries(customFieldsOf(asset));

                return (
                    <li key={key} className='border border-grey-20 rounded-md p-4 flex flex-col gap-2' data-testid={`asset-${key}`}>
                        <div className='flex items-center gap-2 flex-wrap'>
                            <span className='font-semibold text-primary'>{key}</span>
                            {isTileSource(asset) && (
                                <Badge variant='secondary' data-testid={`asset-tile-source-${key}`}>
                                    Tile source
                                </Badge>
                            )}
                            {asset.type && (
                                <span
                                    className='rounded-full bg-secondary px-2 py-0.5 text-[11px] text-muted-foreground'
                                    title={asset.type}
                                    data-testid={`asset-media-type-${key}`}
                                >
                                    {mediaTypeLabel(asset.type)}
                                </span>
                            )}
                            {(asset.roles ?? []).map(role => (
                                <span key={role} className='rounded-full bg-grey-10 px-2 py-0.5 text-[11px]'>
                                    {role}
                                </span>
                            ))}
                        </div>
                        {asset.title && <p className='text-sm text-primary'>{asset.title}</p>}
                        <AssetHref assetKey={key} href={asset.href} />
                        {custom.length > 0 && (
                            <dl className='flex flex-wrap gap-x-4 gap-y-1 text-xs text-grey-70' data-testid={`asset-custom-fields-${key}`}>
                                {custom.map(([field, value]) => (
                                    <div key={field} className='flex gap-1'>
                                        <dt className='font-medium'>{field}</dt>
                                        <dd className='break-all'>{renderCustomValue(value)}</dd>
                                    </div>
                                ))}
                            </dl>
                        )}
                    </li>
                );
            })}
        </ul>
    );
};

export default AssetTable;
